import { v } from "convex/values";

export const widgetType = v.union(
  v.literal("stat_card"),
  v.literal("bar_chart"),
  v.literal("line_chart"),
  v.literal("pie_chart"),
  v.literal("area_chart"),
  v.literal("recent_list"),
  v.literal("ai_pulse"),
  // IoT (MQTT): ultimo valor, historial y boton que publica.
  v.literal("iot_value"),
  v.literal("iot_chart"),
  v.literal("iot_button"),
  v.literal("iot_map"),
  // Dispositivos de un filtro con `+`: lista, resumen de red y tarjeta de uno.
  v.literal("iot_devices"),
  v.literal("iot_network"),
  v.literal("iot_device"),
  // Valor que publica un script en `globals.dashboard`.
  v.literal("script_value"),
  // Registros de una coleccion propia: valor, agrupado o lista.
  v.literal("collection"),
);

export const widgetDataSource = v.union(
  v.literal("quotations"),
  v.literal("clients"),
  v.literal("products"),
  v.literal("sales"),
  v.literal("invoices"),
  v.literal("inventory"),
  v.literal("work_orders"),
  v.literal("ai"),
  v.literal("iot"),
  v.literal("script"),
  v.literal("collection"),
);

export const widgetSize = v.union(
  v.literal("sm"),
  v.literal("md"),
  v.literal("lg"),
  v.literal("xl"),
);

export const widgetTimeRange = v.union(
  v.literal("all"),
  v.literal("today"),
  v.literal("this_week"),
  v.literal("this_month"),
  v.literal("last_7_days"),
  v.literal("last_30_days"),
  v.literal("last_3_months"),
  v.literal("last_6_months"),
);

/** Umbral de un widget IoT: `value` superado (above) o no alcanzado (below). */
export const iotThreshold = v.object({
  op: v.union(v.literal("above"), v.literal("below")),
  value: v.number(),
  level: v.union(v.literal("ok"), v.literal("info"), v.literal("warning"), v.literal("critical")),
  /** Token de la paleta (`chart-N`); vacio = color del nivel. */
  color: v.optional(v.string()),
  label: v.optional(v.string()),
});

/** Configuracion de un widget IoT (`dataSource: "iot"`). */
export const iotWidgetConfig = v.object({
  /** Conexion MQTT (id como texto: el widget sobrevive si se borra). */
  connectionId: v.string(),
  topic: v.string(),
  /** Campo numerico del JSON (`env.temp`); vacio = el valor del mensaje. */
  field: v.optional(v.string()),
  unit: v.optional(v.string()),
  decimals: v.optional(v.number()),
  /** Rango para la barra del valor y el eje del grafico. */
  min: v.optional(v.number()),
  max: v.optional(v.number()),
  /** Umbral (formato anterior; equivale a un umbral "warning"). */
  warnAbove: v.optional(v.number()),
  warnBelow: v.optional(v.number()),
  /** Umbrales con severidad y color (gana el de mayor severidad que se cumpla). */
  thresholds: v.optional(v.array(iotThreshold)),
  /** Color de acento: token de la paleta de graficos (`chart-1`..`chart-12`). */
  color: v.optional(v.string()),
  /** Mapa: campo numerico que colorea el marcador segun los umbrales (`batt`). */
  alertField: v.optional(v.string()),
  /** Grafico: ventana de historial. */
  range: v.optional(
    v.union(v.literal("1h"), v.literal("6h"), v.literal("24h"), v.literal("7d"), v.literal("30d")),
  ),
  /** Mapa: campos de latitud/longitud (vacio = deteccion automatica). */
  latField: v.optional(v.string()),
  lonField: v.optional(v.string()),
  /** Mapa: dibujar el recorrido de la ventana `range`. */
  trail: v.optional(v.boolean()),
  /** Boton: topic y payload a publicar (topic vacio = el del widget). */
  publishTopic: v.optional(v.string()),
  publishPayload: v.optional(v.string()),
  /** Boton de dos estados: payload al apagar (si no, es un boton simple). */
  publishPayloadOff: v.optional(v.string()),
  retain: v.optional(v.boolean()),
  confirm: v.optional(v.boolean()),
  /** Dispositivos: minutos sin mensajes para "sin reporte" y bateria baja (%). */
  staleMinutes: v.optional(v.number()),
  lowBattery: v.optional(v.number()),
  /** Tarjeta de dispositivo: clave del dispositivo. */
  device: v.optional(v.string()),
});

/** Widget "Valor de script" (`dataSource: "script"`): clave de `globals.dashboard`. */
export const scriptWidgetConfig = v.object({
  key: v.string(),
  unit: v.optional(v.string()),
  decimals: v.optional(v.number()),
  min: v.optional(v.number()),
  max: v.optional(v.number()),
  thresholds: v.optional(v.array(iotThreshold)),
  color: v.optional(v.string()),
});

/** Filtro sobre `values[campo]` (mismo formato que la tabla de la coleccion). */
export const collectionWidgetFilter = v.object({
  fieldKey: v.string(),
  type: v.union(v.literal("multi"), v.literal("equals"), v.literal("range"), v.literal("bool")),
  values: v.optional(v.array(v.string())),
  min: v.optional(v.number()),
  max: v.optional(v.number()),
  value: v.optional(v.boolean()),
});

/** Widget "Coleccion" (`dataSource: "collection"`), ver `lib/collections/widget_data.ts`. */
export const collectionWidgetConfig = v.object({
  /** Id como texto: el widget sobrevive (vacio) si se borra la coleccion. */
  collectionId: v.string(),
  mode: v.union(v.literal("value"), v.literal("group"), v.literal("list"), v.literal("trend")),
  /** Grafico: agrupado `bars` (barras horizontales), `bar` o `pie`;
   *  tendencia `line`, `area` o `bar`. Sin valor: el primero de cada modo. */
  chart: v.optional(
    v.union(v.literal("bars"), v.literal("bar"), v.literal("pie"), v.literal("line"), v.literal("area")),
  ),
  /** Tendencia: campo de fecha (vacio = fecha de creacion del registro). */
  dateField: v.optional(v.string()),
  bucket: v.optional(v.union(v.literal("day"), v.literal("week"), v.literal("month"))),
  /** Tendencia: cantidad de periodos hacia atras (incluye el actual). */
  periods: v.optional(v.number()),
  aggregate: v.optional(
    v.union(v.literal("count"), v.literal("sum"), v.literal("avg"), v.literal("min"), v.literal("max")),
  ),
  field: v.optional(v.string()),
  groupBy: v.optional(v.string()),
  filters: v.optional(v.array(collectionWidgetFilter)),
  columns: v.optional(v.array(v.string())),
  sortField: v.optional(v.string()),
  sortDir: v.optional(v.union(v.literal("asc"), v.literal("desc"))),
  limit: v.optional(v.number()),
  unit: v.optional(v.string()),
  decimals: v.optional(v.number()),
  min: v.optional(v.number()),
  max: v.optional(v.number()),
  thresholds: v.optional(v.array(iotThreshold)),
  color: v.optional(v.string()),
  /** Agrupado: color (token `chart-N`) por valor del campo agrupado. */
  valueColors: v.optional(v.array(v.object({ value: v.string(), color: v.string() }))),
});

export const widgetValidator = v.object({
  id: v.string(),
  type: widgetType,
  dataSource: widgetDataSource,
  metric: v.string(),
  title: v.string(),
  size: widgetSize,
  order: v.number(),
  timeRange: v.optional(widgetTimeRange),
  iot: v.optional(iotWidgetConfig),
  script: v.optional(scriptWidgetConfig),
  collection: v.optional(collectionWidgetConfig),
});

export const dashboardConfigValidator = v.object({
  widgets: v.array(widgetValidator),
  columns: v.number(),
});

export const WIDGET_SIZE_COLS: Record<string, number> = {
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
};

export const TIME_RANGE_LABELS: Record<string, string> = {
  all: "Todo el tiempo",
  today: "Hoy",
  this_week: "Esta semana",
  this_month: "Este mes",
  last_7_days: "Últimos 7 días",
  last_30_days: "Últimos 30 días",
  last_3_months: "Últimos 3 meses",
  last_6_months: "Últimos 6 meses",
};

export function getTimeRangeStart(timeRange: string): number | null {
  if (timeRange === "all") return null;

  const now = new Date();

  switch (timeRange) {
    case "today": {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return start.getTime();
    }
    case "this_week": {
      const start = new Date(now);
      const day = start.getDay();
      const diff = day === 0 ? 6 : day - 1;
      start.setDate(start.getDate() - diff);
      start.setHours(0, 0, 0, 0);
      return start.getTime();
    }
    case "this_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return start.getTime();
    }
    case "last_7_days":
      return Date.now() - 7 * 24 * 60 * 60 * 1000;
    case "last_30_days":
      return Date.now() - 30 * 24 * 60 * 60 * 1000;
    case "last_3_months":
      return Date.now() - 90 * 24 * 60 * 60 * 1000;
    case "last_6_months":
      return Date.now() - 180 * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
}

export { AVAILABLE_METRICS } from "@heskala/core/dashboard/metrics";

export const DEFAULT_MOBILE_DASHBOARD_CONFIG = {
  columns: 2,
  widgets: [
    // Resumen de cotizaciones (el modulo mas maduro en mobile). Los stat_card
    // consecutivos se renderizan en filas de 2 columnas en mobile.
    {
      id: "mobile-quotations-total",
      type: "stat_card" as const,
      dataSource: "quotations" as const,
      metric: "total",
      title: "Total cotizaciones",
      size: "sm" as const,
      order: 0,
      timeRange: "this_month" as const,
    },
    {
      id: "mobile-quotations-pending",
      type: "stat_card" as const,
      dataSource: "quotations" as const,
      metric: "pendingCount",
      title: "Pendientes",
      size: "sm" as const,
      order: 1,
      timeRange: "this_month" as const,
    },
    {
      id: "mobile-quotations-status-pie",
      type: "pie_chart" as const,
      dataSource: "quotations" as const,
      metric: "byStatus",
      title: "Cotizaciones por estado",
      size: "md" as const,
      order: 2,
      timeRange: "this_month" as const,
    },
    {
      id: "mobile-quotations-trend",
      type: "line_chart" as const,
      dataSource: "quotations" as const,
      metric: "monthlyTrend",
      title: "Tendencia mensual",
      size: "md" as const,
      order: 3,
      timeRange: "last_6_months" as const,
    },
    {
      id: "mobile-quotations-recent",
      type: "recent_list" as const,
      dataSource: "quotations" as const,
      metric: "recentList",
      title: "Cotizaciones recientes",
      size: "md" as const,
      order: 4,
      timeRange: "last_30_days" as const,
    },
    // Clientes y productos: stat_card consecutivos para layout de 2 columnas.
    {
      id: "mobile-clients-total",
      type: "stat_card" as const,
      dataSource: "clients" as const,
      metric: "total",
      title: "Clientes",
      size: "sm" as const,
      order: 5,
      timeRange: "all" as const,
    },
    {
      id: "mobile-invoices-total",
      type: "stat_card" as const,
      dataSource: "invoices" as const,
      metric: "total",
      title: "Facturas",
      size: "sm" as const,
      order: 6,
      timeRange: "this_month" as const,
    },
    {
      id: "mobile-products-total",
      type: "stat_card" as const,
      dataSource: "products" as const,
      metric: "total",
      title: "Productos / Servicios",
      size: "sm" as const,
      order: 7,
      timeRange: "all" as const,
    },
    {
      id: "mobile-clients-active-pie",
      type: "pie_chart" as const,
      dataSource: "clients" as const,
      metric: "activeVsInactive",
      title: "Clientes activos",
      size: "sm" as const,
      order: 8,
      timeRange: "all" as const,
    },
    {
      id: "mobile-products-type-pie",
      type: "pie_chart" as const,
      dataSource: "products" as const,
      metric: "byType",
      title: "Productos por tipo",
      size: "sm" as const,
      order: 9,
      timeRange: "all" as const,
    },
  ],
};

export const DEFAULT_DASHBOARD_CONFIG = {
  columns: 4,
  widgets: [
    // Fila 1: Stat cards principales
    {
      id: "default-quotations-total",
      type: "stat_card" as const,
      dataSource: "quotations" as const,
      metric: "total",
      title: "Total Cotizaciones",
      size: "sm" as const,
      order: 0,
      timeRange: "this_month" as const,
    },
    {
      id: "default-clients-total",
      type: "stat_card" as const,
      dataSource: "clients" as const,
      metric: "total",
      title: "Clientes Activos",
      size: "sm" as const,
      order: 1,
      timeRange: "all" as const,
    },
    {
      id: "default-products-total",
      type: "stat_card" as const,
      dataSource: "products" as const,
      metric: "total",
      title: "Productos/Servicios",
      size: "sm" as const,
      order: 2,
      timeRange: "all" as const,
    },
    {
      id: "default-quotations-conversion",
      type: "stat_card" as const,
      dataSource: "quotations" as const,
      metric: "conversionRate",
      title: "Tasa de Conversion",
      size: "sm" as const,
      order: 3,
      timeRange: "this_month" as const,
    },
    // Fila 2: Stat cards secundarios
    {
      id: "default-invoices-total",
      type: "stat_card" as const,
      dataSource: "invoices" as const,
      metric: "total",
      title: "Total Facturas",
      size: "sm" as const,
      order: 4,
      timeRange: "this_month" as const,
    },
    {
      id: "default-invoices-pending",
      type: "stat_card" as const,
      dataSource: "invoices" as const,
      metric: "pendingCount",
      title: "Facturas Pendientes",
      size: "sm" as const,
      order: 5,
      timeRange: "this_month" as const,
    },
    {
      id: "default-quotations-pending",
      type: "stat_card" as const,
      dataSource: "quotations" as const,
      metric: "pendingCount",
      title: "Cotizaciones Pendientes",
      size: "sm" as const,
      order: 6,
      timeRange: "this_month" as const,
    },
    {
      id: "default-sales-total-amount",
      type: "stat_card" as const,
      dataSource: "sales" as const,
      metric: "totalAmount",
      title: "Monto Total Ventas",
      size: "sm" as const,
      order: 7,
      timeRange: "this_month" as const,
    },
    // Fila 3: Pie charts
    {
      id: "default-quotations-status",
      type: "pie_chart" as const,
      dataSource: "quotations" as const,
      metric: "byStatus",
      title: "Total Cotizaciones",
      size: "sm" as const,
      order: 8,
      timeRange: "this_month" as const,
    },
    {
      id: "default-invoices-status",
      type: "pie_chart" as const,
      dataSource: "invoices" as const,
      metric: "byStatus",
      title: "Total Facturas",
      size: "sm" as const,
      order: 9,
      timeRange: "this_month" as const,
    },
    {
      id: "default-work-orders-status",
      type: "pie_chart" as const,
      dataSource: "work_orders" as const,
      metric: "byStatus",
      title: "Total Ordenes",
      size: "sm" as const,
      order: 10,
      timeRange: "this_month" as const,
    },
    {
      id: "default-inventory-alerts",
      type: "pie_chart" as const,
      dataSource: "inventory" as const,
      metric: "alertDistribution",
      title: "Productos con Stock",
      size: "sm" as const,
      order: 11,
      timeRange: "this_month" as const,
    },
    // Fila 4: Recent lists
    {
      id: "default-quotations-recent",
      type: "recent_list" as const,
      dataSource: "quotations" as const,
      metric: "recentList",
      title: "Cotizaciones Recientes",
      size: "sm" as const,
      order: 12,
      timeRange: "last_30_days" as const,
    },
    {
      id: "default-invoices-recent",
      type: "recent_list" as const,
      dataSource: "invoices" as const,
      metric: "recentList",
      title: "Facturas Recientes",
      size: "sm" as const,
      order: 13,
      timeRange: "last_30_days" as const,
    },
    {
      id: "default-sales-recent",
      type: "recent_list" as const,
      dataSource: "sales" as const,
      metric: "recentList",
      title: "Ventas Recientes",
      size: "sm" as const,
      order: 14,
      timeRange: "last_30_days" as const,
    },
    {
      id: "default-products-recent",
      type: "recent_list" as const,
      dataSource: "products" as const,
      metric: "recentList",
      title: "Productos Recientes",
      size: "sm" as const,
      order: 15,
      timeRange: "all" as const,
    },
  ],
};
